import { Icons } from "assets";
import classNames from "classnames";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import NoContentCard from "shared/components/common/noContentCard";
import { toastMessage } from "shared/components/common/toast";
import ComplaintSideCanvas from "shared/components/complaintSideCanvas";
import SekeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import { setCountReducer } from "shared/redux/reducers/count";
import {
  DeleteComplaintService,
  GetComplaintsService,
  GetCountService,
} from "shared/services/general";
import styles from "./styles.module.scss";

const Heads: CustomTableHeadType[] = [
  {
    title: "User",
    key: "user.name",
  },
  {
    title: "Organization",
    key: "user.organization.name",
  },
  {
    title: "Subject",
    key: "subject",
  },
  {
    title: "Message",
    key: "msg",
  },
  {
    title: "Date",
    key: "created_at",
  },
  {
    title: "Action",
    key: "",
  },
];

const ContactSupport = () => {
  const dispatch = useDispatch();

  const [showComplaint, setShowComplaint] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [complaints, setComplaints] = useState<any[]>([]);
  const [selectedComplaint, setSelectedComplaint] = useState<any>(null);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [delLoading, setDelLoading] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const handleShowConfirmation = () => {
    setShowConfirmation(true);
  };

  const handleHideConfirmation = () => {
    setShowConfirmation(false);
  };

  const handleComplaints = () => {
    setLoading(true);
    GetComplaintsService({ pageNo: page })
      .then(({ data: { data, total } }) => {
        setComplaints(data);
        setTotal(total);
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const handleGetCount = () => {
    GetCountService()
      .then(({ data }) => {
        dispatch(
          setCountReducer({
            complaints: data?.complaints_count,
            orgs: data?.orgs_count,
            req: data?.req_count,
          })
        );
      })
      .catch((err) => {});
  };

  const handleDeleteComplaint = () => {
    setDelLoading(true);
    DeleteComplaintService(selectedComplaint?.id)
      .then(() => {
        toastMessage("success", "Complaint deleted successfully");
        let filtered = complaints.filter(
          (item) => item?.id !== selectedComplaint?.id
        );
        handleGetCount();
        setComplaints(filtered);
        handleHideConfirmation();
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
      })
      .finally(() => {
        setDelLoading(false);
      });
  };

  useEffect(() => {
    handleGetCount();
    handleComplaints();
    // eslint-disable-next-line
  }, []);

  return (
    <div className={classNames(styles.mainContainer, "relative")}>
      <CustomTable
        title="Organizations List"
        heads={Heads}
        loading={loading}
        isEmpty={complaints.length === 0}
        stickSecondCol={false}
        evaluations={complaints}
        setEvaluations={setComplaints}
        stickFirstCol={false}
        maxWidth="900px"
      >
        {loading ? (
          <>
            {Array.from({ length: 4 }).map((_, inx) => {
              return <RowItemLoader key={inx} />;
            })}
          </>
        ) : complaints?.length > 0 ? (
          <>
            {complaints?.map((item, inx) => {
              return (
                <RowItem
                  item={item}
                  key={item?.id}
                  isLast={inx === complaints?.length - 1}
                  setShowComplaint={setShowComplaint}
                  setSelectedComplaint={setSelectedComplaint}
                  handleShowConfirmation={handleShowConfirmation}
                />
              );
            })}
          </>
        ) : (
          <div className={classNames("my-10")}>
            <NoContentCard
              label1={"No Complaints found"}
              label2="There are no complaints submitted by users!"
            />
          </div>
        )}
      </CustomTable>

      <div
        className={classNames(
          "w-full px-4 py-4",
          total >= 10 && styles.border,
          styles.paginationContainer
        )}
      >
        <CustomPagination
          currentPage={page}
          totalCount={total}
          pageSize={10}
          onPageChange={(page: any) => {
            setPage(page);
            handleComplaints();
          }}
        />
      </div>
      <ComplaintSideCanvas
        isOpen={showComplaint}
        setIsOpen={setShowComplaint}
        data={selectedComplaint}
      />
      <ConfirmationModal
        show={showConfirmation}
        handleClose={handleHideConfirmation}
        loading={delLoading}
        handleSubmit={handleDeleteComplaint}
      />
    </div>
  );
};

const RowItem = ({
  item,
  isLast,
  setShowComplaint,
  setSelectedComplaint,
  handleShowConfirmation,
}: any) => {
  return (
    <tr
      className={classNames(
        styles.singleComplaint,
        !isLast && styles.activeBorder
      )}
    >
      <td className={classNames(styles.tableContentLabel, "ps-4 pe-2 py-3")}>
        <div className={classNames("flex items-center gap-2 justify-center")}>
          <div className={classNames(styles.avtContainer)}>
            <label>
              {item?.user?.name?.[0]}
              {item?.user?.name?.split(" ")?.[1]?.[0]}
            </label>
          </div>
          <label>{item?.user?.name}</label>
        </div>
      </td>

      <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
        <label>{item?.user?.organization?.name}</label>
      </td>

      <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
        <label>{item?.subject}</label>
      </td>

      <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
        <label className={classNames(styles.msg)}>{item?.msg}</label>
      </td>

      <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
        <label className={classNames(styles.dateLabel)}>
          {moment(item?.created_at).format("MMM DD, YYYY")}
        </label>
      </td>

      <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
        <div className={classNames("flex items-center gap-2 justify-center")}>
          <CustomToolTip label="View Detail">
            <div
              className={classNames(styles.actionContainer)}
              onClick={() => {
                setSelectedComplaint(item);
                setShowComplaint(true);
              }}
            >
              <Icons.Eye />
            </div>
          </CustomToolTip>
          <CustomToolTip label="Delete">
            <div
              className={classNames(styles.actionContainer)}
              onClick={() => {
                setSelectedComplaint(item);
                handleShowConfirmation();
              }}
            >
              <Icons.TrashOutline2 />
            </div>
          </CustomToolTip>
        </div>
      </td>
    </tr>
  );
};

const RowItemLoader = () => {
  return (
    <tr className={classNames(styles.singleComplaint)}>
      <td className={classNames(styles.tableContentLabel, "ps-4 pe-2 py-3")}>
        <div className={classNames("flex items-center gap-2 justify-center")}>
          <SekeletonLoader className={classNames(styles.avtContainerLoader)} />
          <SekeletonLoader className={classNames(styles.userTitleLoader)} />
        </div>
      </td>

      <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
        <SekeletonLoader className={classNames(styles.userTitleLoader)} />
      </td>

      <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
        <SekeletonLoader className={classNames(styles.userTitleLoader)} />
      </td>

      <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
        <SekeletonLoader className={classNames(styles.paraLoader)} />
      </td>

      <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
        <SekeletonLoader className={classNames(styles.paraLoader)} />
      </td>

      <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
        <div className={classNames("flex items-center gap-2 justify-center")}>
          <SekeletonLoader className={classNames(styles.actionLoader)} />
          <SekeletonLoader className={classNames(styles.actionLoader)} />
        </div>
      </td>
    </tr>
  );
};
export default ContactSupport;
